/* eslint-disable array-callback-return */
import { SearchOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Col, Row, Modal, Checkbox } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, SubmitButton, Select, DatePicker, Input } from 'formik-antd';
import moment, { Moment } from 'moment';
import { resetSearchValue } from 'pages/ListMachine/slices';
import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { HeaderTitle, ModalWarningLeavePage, Rectangle } from 'components';
import { t_equipment_lease, t_machine } from 'types';
import { RootState } from '../../store';
import { updateMachine } from '../ListMachine/thunk';
import { getWorkspace } from '../LoginPage/thunk';
import { MachineSchema, MachineSchemaChecked } from '../MachineRegister/schema';
import { getDataStoreFieldEquipment, getDataStoreFieldMachine } from '../MachineRegister/thunk';
import TableEdit from './TableEdit';
import Wrap from './styles';
import { deleteEquiment } from './thunk';

type InitialValues = {
  lease_start_date?: string | null;
  lease_end_date: string | null;
  lease_contract_date: string;
  lease_company: string | undefined;
  lease_shop: string | undefined;
  lease_shop_other: string;
  lease_contract_id: string;
  machine_type: string;
  machine_serial_number: string;
  machine_other: string;
  machine_quantity: number;
};

const MachineEdit: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [modalConfirmVisible, setModalConfirmVisible] = useState<boolean>(false);
  const [modalDoneVisible, setModalDoneVisible] = useState<boolean>(false);
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);
  const { datastoreEquipmentFields } = useSelector((state: RootState) => state.machineequipment);
  const { itemDetail } = useSelector((state: RootState) => state.machine);
  const [optionCompany, setOptionCompany] = useState<string>(itemDetail?.lease_company || '');
  const [optionShop, setOptionShop] = useState<string>(itemDetail?.lease_shop || '');
  const [startDate, setStartDate] = useState<string>(itemDetail?.lease_start_date || '');
  const [endDate, setEndDate] = useState<string>(itemDetail?.lease_end_date || '');
  const [leaseDate, setLeaseDate] = useState<string>(itemDetail?.lease_contract_date || '');
  const [leaseId, setLeaseId] = useState<string>(itemDetail?.lease_contract_id || '');
  const [data, setData] = useState<t_machine[]>(itemDetail?.machines || []);
  const [checked, setChecked] = useState(itemDetail?.purchased_equipment === '1');

  const initialValues: InitialValues = {
    lease_start_date: itemDetail?.lease_start_date || null,
    lease_end_date: itemDetail?.lease_end_date || null,
    lease_contract_date: itemDetail?.lease_contract_date || '',
    lease_company: optionCompany,
    lease_shop: optionShop,
    lease_shop_other: itemDetail?.lease_shop_other || '',
    lease_contract_id: itemDetail?.lease_contract_id || '',
    machine_type: '',
    machine_serial_number: '',
    machine_other: '',
    machine_quantity: itemDetail?.machine_quantity || 0,
  };
  useEffect(() => {
    dispatch(getWorkspace());
  }, [dispatch]);
  useEffect(() => {
    return () => {
      if (history.location.pathname !== '/machine' && history.location.pathname !== '/machine/detail') {
        dispatch(resetSearchValue());
      }
    };
  }, [dispatch, history.location.pathname]);

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFieldMachine({ appId: currentApplicationId, dataStoreId: 't_machine' }));
  }, [dispatch, currentApplicationId]);

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFieldEquipment({ appId: currentApplicationId, dataStoreId: 't_equipment_lease' }));
  }, [dispatch, currentApplicationId]);

  const optionCompanyLease = useMemo(() => {
    return (
      datastoreEquipmentFields?.find(([_key, value]: [string, any]) => value.display_id === 'lease_company')[1]
        .options || []
    );
  }, [datastoreEquipmentFields]);

  const optionShopLease = useMemo(() => {
    return (
      datastoreEquipmentFields?.find(([_key, value]: [string, any]) => value.display_id === 'lease_shop')[1].options ||
      []
    );
  }, [datastoreEquipmentFields]);

  const onChangeStartDate = (value: any, dateString: string) => {
    setStartDate(dateString);
  };

  const validateStartDate = (dateString: Moment) => {
    return dateString && dateString > moment(endDate).startOf('day');
  };

  const validateEndDate = (dateString: Moment) => {
    return dateString && dateString < moment(startDate).endOf('day');
  };

  const onChangeEndDate = (value: any, dateString: string) => {
    setEndDate(dateString);
  };

  const onChangeLeaseDate = (value: any, dateString: string) => {
    setLeaseDate(dateString);
  };

  const handleSubmit = useCallback(
    async (values: InitialValues, form: FormikHelpers<InitialValues>) => {
      const body: t_equipment_lease = {
        lease_start_date: startDate ? startDate : null,
        lease_end_date: endDate ? endDate : null,
        lease_contract_date: leaseDate,
        lease_company:
          optionCompany === itemDetail?.lease_company && optionCompany.length > 0
            ? optionCompanyLease.find((item: any) => item.value === optionCompany).display_id
            : optionCompany
            ? optionCompanyLease.find((item: any) => item.display_id === optionCompany).display_id
            : '',
        machine_quantity: data.length,
        lease_shop:
          optionShop === itemDetail?.lease_shop
            ? optionShopLease.find((item: any) => item.value === optionShop).display_id
            : optionShopLease.find((item: any) => item.display_id === optionShop).display_id,
        lease_shop_other: values.lease_shop_other,
        lease_contract_id: leaseId,
        i_id: itemDetail?.i_id,
        d_id: itemDetail?.d_id,
        operation: 2,
        purchased_equipment: checked ? 1 : undefined,
      };
      let formatData: any = [];
      data.map((m: t_machine) => {
        if (m.machine_type !== 'o_other') {
          const newMachine = Object.assign({}, m);
          newMachine.machine_other = '';
          formatData.push(newMachine);
        } else {
          formatData.push(m);
        }
      });
      const result: any = await dispatch(updateMachine({ item: body, machine: formatData, return_item_result: true }));
      form.setSubmitting(false);
      if (!result.payload.error) {
        history.push('/machine/detail', { itemId: itemDetail?.i_id, omitWarningLeavePage: true });
      }
    },
    [
      startDate,
      endDate,
      leaseDate,
      optionCompany,
      itemDetail?.lease_company,
      itemDetail?.lease_shop,
      itemDetail?.i_id,
      itemDetail?.d_id,
      optionCompanyLease,
      data,
      optionShop,
      optionShopLease,
      leaseId,
      dispatch,
      history,
      checked,
    ],
  );

  const doneModal = async () => {
    const result: any = await dispatch(deleteEquiment(itemDetail?.i_id || ''));
    if (!result.payload.error) {
      setModalConfirmVisible(false);
      setModalDoneVisible(true);
    }
  };
  return (
    <Wrap>
      <HeaderTitle title="リース契約詳細" icon={SearchOutlined} />
      <div className="edit">
        <span className="title-edit extraLargeFont">編集</span>
      </div>
      <div className="delete-lease">
        <Button onClick={() => setModalConfirmVisible(true)} className="btn-delete-lease">
          <DeleteOutlined /> このデータを削除する
        </Button>
        <Modal
          title="リース情報の削除"
          centered
          visible={modalConfirmVisible}
          closable={false}
          onOk={() => doneModal()}
          onCancel={() => setModalConfirmVisible(false)}
          okText="削除する"
          cancelText="キャンセル"
          cancelButtonProps={{ className: 'btnDefault' }}
          okButtonProps={{ className: 'extraLargeFont', style: { textAlign: 'center' } }}
        >
          <p
            className="middleBoldFont"
            style={{
              width: '253px',
              height: '48px',
              textAlign: 'center',
              margin: '0px auto',
              paddingBottom: '104px',
              paddingTop: '21px',
            }}
          >
            このリース契約情報を削除します。 よろしいですか？
          </p>
        </Modal>
        <Modal
          title="リース情報の削除"
          centered
          visible={modalDoneVisible}
          closable={false}
          okButtonProps={{ style: { display: 'none' } }}
          onCancel={() => history.push('/machine')}
          cancelText="閉じる"
          cancelButtonProps={{
            className: 'extraLargeFont',
            style: { textAlign: 'center', backgroundColor: '#d8d8d8', width: '256px', height: '60px' },
          }}
        >
          <p
            className="middleBoldFont"
            style={{
              width: '253px',
              height: '48px',
              textAlign: 'center',
              margin: '0px auto',
              paddingBottom: '80px',
              paddingTop: '21px',
            }}
          >
            削除しました。
          </p>
        </Modal>
      </div>
      <div className="section">
        <Rectangle text={`${itemDetail?.purchased_equipment === '1' ? '購入機器情報' : 'リース情報'}`} />
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={!checked ? MachineSchema : MachineSchemaChecked}
          render={({ isValid }) => (
            <Form autoComplete={`${Math.random()}`}>
              <div className="rental-detail">
                <div className="input-rental-detail">
                  <Form.Item name="check-box">
                    <div className="checked-box">
                      <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                      <span className="title-check-box">購入機器 </span>
                    </div>
                  </Form.Item>
                  <Row className="dflex">
                    <Col className="dflex-col" span={5}>
                      <span className="standardBoldFont">リース期間</span>
                      {!checked && <span className="smallBoldFont ※">※</span>}
                    </Col>
                    <Col className="dflex baseline" span={19}>
                      <Form.Item name="lease_start_date">
                        <DatePicker
                          onChange={onChangeStartDate}
                          disabledDate={validateStartDate}
                          name="lease_start_date"
                          className="input-width-date"
                          placeholder=""
                          size={'large'}
                          locale={{
                            lang: {
                              locale: 'ja',
                              placeholder: 'Select date',
                              rangePlaceholder: ['Start date', 'End date'],
                              today: 'Today1',
                              now: 'Now',
                              backToToday: 'Back to today',
                              ok: 'Ok',
                              clear: 'Clear',
                              month: 'Month',
                              year: 'Year',
                              timeSelect: 'Select time',
                              dateSelect: 'Select date',
                              monthSelect: 'Choose a month',
                              yearSelect: 'Choose a year',
                              decadeSelect: 'Choose a decade',
                              yearFormat: 'YYYY',
                              dateFormat: 'M/D/YYYY',
                              dayFormat: 'D',
                              dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                              monthFormat: 'MMMM',
                              monthBeforeYear: true,
                              previousMonth: 'Previous month (PageUp)',
                              nextMonth: 'Next month (PageDown)',
                              previousYear: 'Last year (Control + left)',
                              nextYear: 'Next year (Control + right)',
                              previousDecade: 'Last decade',
                              nextDecade: 'Next decade',
                              previousCentury: 'Last century',
                              nextCentury: 'Next century',
                            },
                            timePickerLocale: {
                              placeholder: 'Select time',
                            },
                            dateFormat: 'YYYY-MM-DD',
                            dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                            weekFormat: 'YYYY-wo',
                            monthFormat: 'YYYY-MM',
                          }}
                        />
                      </Form.Item>
                      <p className="mg-10 standardBoldFont">〜</p>
                      <Form.Item name="lease_end_date">
                        <DatePicker
                          onChange={onChangeEndDate}
                          disabledDate={validateEndDate}
                          name="lease_end_date"
                          className="input-width-date"
                          placeholder=""
                          size={'large'}
                          locale={{
                            lang: {
                              locale: 'ja',
                              placeholder: 'Select date',
                              rangePlaceholder: ['Start date', 'End date'],
                              today: 'Today1',
                              now: 'Now',
                              backToToday: 'Back to today',
                              ok: 'Ok',
                              clear: 'Clear',
                              month: 'Month',
                              year: 'Year',
                              timeSelect: 'Select time',
                              dateSelect: 'Select date',
                              monthSelect: 'Choose a month',
                              yearSelect: 'Choose a year',
                              decadeSelect: 'Choose a decade',
                              yearFormat: 'YYYY',
                              dateFormat: 'M/D/YYYY',
                              dayFormat: 'D',
                              dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                              monthFormat: 'MMMM',
                              monthBeforeYear: true,
                              previousMonth: 'Previous month (PageUp)',
                              nextMonth: 'Next month (PageDown)',
                              previousYear: 'Last year (Control + left)',
                              nextYear: 'Next year (Control + right)',
                              previousDecade: 'Last decade',
                              nextDecade: 'Next decade',
                              previousCentury: 'Last century',
                              nextCentury: 'Next century',
                            },
                            timePickerLocale: {
                              placeholder: 'Select time',
                            },
                            dateFormat: 'YYYY-MM-DD',
                            dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                            weekFormat: 'YYYY-wo',
                            monthFormat: 'YYYY-MM',
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row className="dflex mt-10">
                    <Col className="dflex-col" span={5}>
                      <span className="standardBoldFont">契約日</span>
                      <span className="smallBoldFont ※">※</span>
                    </Col>
                    <Col className="dflex baseline" span={19}>
                      <Form.Item name="lease_contract_date">
                        <DatePicker
                          onChange={onChangeLeaseDate}
                          name="lease_contract_date"
                          className="input-width-date"
                          placeholder=""
                          size={'large'}
                          locale={{
                            lang: {
                              locale: 'ja',
                              placeholder: 'Select date',
                              rangePlaceholder: ['Start date', 'End date'],
                              today: 'Today1',
                              now: 'Now',
                              backToToday: 'Back to today',
                              ok: 'Ok',
                              clear: 'Clear',
                              month: 'Month',
                              year: 'Year',
                              timeSelect: 'Select time',
                              dateSelect: 'Select date',
                              monthSelect: 'Choose a month',
                              yearSelect: 'Choose a year',
                              decadeSelect: 'Choose a decade',
                              yearFormat: 'YYYY',
                              dateFormat: 'M/D/YYYY',
                              dayFormat: 'D',
                              dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                              monthFormat: 'MMMM',
                              monthBeforeYear: true,
                              previousMonth: 'Previous month (PageUp)',
                              nextMonth: 'Next month (PageDown)',
                              previousYear: 'Last year (Control + left)',
                              nextYear: 'Next year (Control + right)',
                              previousDecade: 'Last decade',
                              nextDecade: 'Next decade',
                              previousCentury: 'Last century',
                              nextCentury: 'Next century',
                            },
                            timePickerLocale: {
                              placeholder: 'Select time',
                            },
                            dateFormat: 'YYYY-MM-DD',
                            dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                            weekFormat: 'YYYY-wo',
                            monthFormat: 'YYYY-MM',
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row className="dflex mt-20">
                    <Col className="dflex-col" span={5}>
                      <span className="standardBoldFont">台数</span>
                    </Col>
                    <Col className="dflex" span={19}>
                      <Form.Item name="lease_company">{itemDetail?.machine_quantity}台</Form.Item>
                    </Col>
                  </Row>
                  <Row className="dflex mt-20">
                    <Col className="dflex-col" span={5}>
                      <span className="standardBoldFont">リース会社</span>
                      {!checked && <span className="smallBoldFont ※">※</span>}
                    </Col>
                    <Col className="dflex" span={19}>
                      <Form.Item name="lease_company">
                        <Select
                          name="lease_company"
                          value={optionCompany}
                          allowClear
                          onChange={(e) => setOptionCompany(e)}
                          className="input-width-select"
                          size={'large'}
                        >
                          {optionCompanyLease.map((g: any, i: number) => (
                            <Select.Option key={i} value={g.display_id}>
                              {g.value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row className="dflex mt-20">
                    <Col className="dflex-col" span={5}>
                      <span className="standardBoldFont">販売店</span>
                      <span className="smallBoldFont ※">※</span>
                    </Col>
                    <Col className="dflex" span={19}>
                      <Form.Item name="lease_shop">
                        <Select
                          name="lease_shop"
                          allowClear
                          value={optionShop}
                          onChange={(e) => setOptionShop(e)}
                          className="input-width-select"
                          size={'large'}
                        >
                          {optionShopLease.map((g: any, i: number) => (
                            <Select.Option key={i} value={g.display_id}>
                              {g.value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item name="lease_shop_other">
                        <Input
                          name="lease_shop_other"
                          className="input-width ml-10"
                          style={{ backgroundColor: optionShop === 'o_other' ? '#fff' : '#dddddd' }}
                          disabled={optionShop === 'o_other' ? false : true}
                          size={'large'}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row className="dflex mt-20">
                    <Col className="dflex-col" span={5}>
                      <span className="standardBoldFont">契約番号</span>
                      <span className="smallBoldFont ※">※</span>
                    </Col>
                    <Col className="dflex" span={19}>
                      <Form.Item name="lease_contract_id">
                        <Input
                          name="lease_contract_id"
                          className="input-width-select"
                          size={'large'}
                          onChange={(e) => setLeaseId(e.target.value)}
                          autoComplete={`${Math.random()}`}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="table-add-detail">
                <Rectangle text="リース機器一覧" />
                <TableEdit dataSource={data} setData={setData} />
              </div>
              {!isValid && (
                <div style={{ marginTop: '0.5rem' }} className="error">
                  <WarningOutlined />
                  <p>入力内容に不備がございます。エラーが表示されている項目をご確認ください。</p>
                </div>
              )}
              <div className="form-footer">
                <Button
                  onClick={() => history.push('/machine/detail', { itemId: itemDetail?.i_id })}
                  className="btn-back"
                >
                  キャンセル
                </Button>
                <SubmitButton className="btn-confirm-register middleBoldFont">更新する</SubmitButton>
              </div>
            </Form>
          )}
        />
      </div>
      <ModalWarningLeavePage />
    </Wrap>
  );
};

export default MachineEdit;
